/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  useTheme,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { updateExistingLeaveType } from "store/features/company/leaveManagement/leaveType/leaveTypeActions";
import editLeaveTypeSvg from "assets/modals/edit.svg";

const EditLeaveTypeModal = ({ isOpen, onClose, leaveType, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    color: "#f59e0b",
    is_paid: 1,
    max_days_per_year: "",
    carry_forward_limit: "",
    requires_approval: 1,
  });

  // Fix: Improved body scroll lock with scrollbar width compensation
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;

      // Get scrollbar width to prevent layout shift
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollbarWidth}px`;

      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (leaveType) {
      setFormData({
        name: leaveType.name || "",
        code: leaveType.code || "",
        color: leaveType.color || "#f59e0b",
        is_paid: leaveType.is_paid !== undefined ? leaveType.is_paid : 1,
        max_days_per_year: leaveType.max_days_per_year || "",
        carry_forward_limit: leaveType.carry_forward_limit || "",
        requires_approval:
          leaveType.requires_approval !== undefined
            ? leaveType.requires_approval
            : 1,
      });
    }
  }, [leaveType]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formattedData = {
      ...formData,
      max_days_per_year: Number(formData.max_days_per_year),
      carry_forward_limit: formData.carry_forward_limit
        ? Number(formData.carry_forward_limit)
        : 0,
      is_paid: Number(formData.is_paid),
      requires_approval: Number(formData.requires_approval),
    };

    const result = await dispatch(
      updateExistingLeaveType(leaveType.id, formattedData),
    );

    if (result.success) {
      onSuccess();
      onClose();
    }
    setLoading(false);
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
      "&.Mui-focused": {
        color: "#f59e0b",
      },
      "& .MuiInputLabel-asterisk": {
        color: "#ef4444",
      },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && leaveType && (
        <>
          <style>
            {`
          .MuiPopover-root {
            z-index: 1400 !important;
          }
          .MuiPopper-root {
            z-index: 1400 !important;
          }
        `}
          </style>

          {/* Backdrop with fade animation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(252, 226, 181, 0.14)"
                  : "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          {/* Modal with slide-in from right animation - Fixed overflow */}
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100vh",
              width: "100%",
              maxWidth: "600px",
              overflowY: "auto",
              overflowX: "hidden",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={editLeaveTypeSvg}
                      alt="Edit Leave Type"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: "contain",
                        transition: "all 0.3s",
                        "&:hover": {
                          filter: "drop-shadow(0 4px 8px rgba(245,158,11,0.3))",
                        },
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontWeight: "bold",
                        color:
                          theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                      }}
                    >
                      Edit Leave Type
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color:
                          theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      Update leave policy information
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f59e0b",
                    color: "#ffffff",
                    transform: "scale(1.05)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Form - Added scrollbarGutter to prevent shift */}
            <form onSubmit={handleSubmit}>
              <Box
                sx={{
                  p: 3,
                  scrollbarGutter: "stable",
                }}
              >
                <Stack spacing={3}>
                  {/* Name and Code Row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Leave Type Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Leave Code"
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                      required
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Stack>

                  {/* Color Picker */}
                  <TextField
                    fullWidth
                    label="Color"
                    name="color"
                    type="color"
                    value={formData.color}
                    onChange={handleChange}
                    required
                    sx={{
                      ...inputSx,
                      "& .MuiOutlinedInput-root": {
                        ...inputSx["& .MuiOutlinedInput-root"],
                        padding: "4px",
                      },
                      "& input[type='color']": {
                        height: "40px",
                        cursor: "pointer",
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />

                  {/* Max Days and Carry Forward Row */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      label="Max Days Per Year"
                      name="max_days_per_year"
                      type="number"
                      value={formData.max_days_per_year}
                      onChange={handleChange}
                      required
                      InputProps={{ inputProps: { min: 0, step: 1 } }}
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Carry Forward Limit"
                      name="carry_forward_limit"
                      type="number"
                      value={formData.carry_forward_limit}
                      onChange={handleChange}
                      InputProps={{ inputProps: { min: 0, step: 1 } }}
                      sx={inputSx}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Stack>

                  {/* Paid Leave and Approval Required Switches */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="is_paid"
                          checked={formData.is_paid === 1}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              is_paid: e.target.checked ? 1 : 0,
                            }))
                          }
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#f59e0b",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "#f59e0b",
                              },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "#e5e7eb"
                                : "#374151",
                          }}
                        >
                          Paid Leave
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          name="requires_approval"
                          checked={formData.requires_approval === 1}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              requires_approval: e.target.checked ? 1 : 0,
                            }))
                          }
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#f59e0b",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "#f59e0b",
                              },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            color:
                              theme.palette.mode === "dark"
                                ? "#e5e7eb"
                                : "#374151",
                          }}
                        >
                          Requires Approval
                        </Typography>
                      }
                    />
                  </Stack>
                </Stack>
              </Box>

              {/* Buttons */}
              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    borderColor:
                      theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                    color:
                      theme.palette.mode === "dark" ? "#d1d5db" : "#374151",
                    "&:hover": {
                      borderColor: "#f59e0b",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(245,158,11,0.1)"
                          : "#fffbeb",
                    },
                  }}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "#ffffff",
                    boxShadow: "0 10px 15px -3px rgba(245, 158, 11, 0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                      transform: "scale(1.02)",
                      boxShadow: "0 20px 25px -5px rgba(245, 158, 11, 0.3)",
                    },
                    "&:disabled": {
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Update Leave Type"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditLeaveTypeModal;
